import type { MergedSymbolInfo } from './dx-trading-mappers'

const DEFAULT_DISPLAY_TICK = 0.00001
const DEFAULT_PRECISION = 5

export interface PrecisionConfig {
  displayTick: number
  maxPrecision: number
}

function isValidDisplayTick(tick: number | undefined): boolean {
  return typeof tick === 'number' && Number.isFinite(tick) && tick > 0
}

function isValidPrecision(precision: number | undefined): boolean {
  return typeof precision === 'number' && Number.isInteger(precision) && precision >= 0 && precision <= 20
}

function resolvePrecisionConfig(symbolInfo: MergedSymbolInfo['symbolInfo'] | undefined): PrecisionConfig {
  if (!symbolInfo) {
    return { displayTick: DEFAULT_DISPLAY_TICK, maxPrecision: DEFAULT_PRECISION }
  }

  let displayTick = DEFAULT_DISPLAY_TICK
  if (isValidDisplayTick(symbolInfo.display_tick_size)) {
    displayTick = symbolInfo.display_tick_size!
  } else if (isValidDisplayTick(symbolInfo.tick_size)) {
    displayTick = symbolInfo.tick_size!
  }

  let maxPrecision = DEFAULT_PRECISION
  if (isValidPrecision(symbolInfo.max_tick_precision)) {
    maxPrecision = symbolInfo.max_tick_precision!
  }

  return { displayTick, maxPrecision }
}

export function getDisplayTick(symbolInfo: MergedSymbolInfo['symbolInfo'] | undefined): number {
  return resolvePrecisionConfig(symbolInfo).displayTick
}

export function getMaxPrecision(symbolInfo: MergedSymbolInfo['symbolInfo'] | undefined): number {
  return resolvePrecisionConfig(symbolInfo).maxPrecision
}

export function roundToDisplayTick(price: number, symbolInfo: MergedSymbolInfo['symbolInfo'] | undefined): number {
  const { displayTick } = resolvePrecisionConfig(symbolInfo)
  const rounded = Math.round(price / displayTick) * displayTick
  return Number(rounded.toFixed(10))
}

export function formatPrice(price: number, symbolInfo: MergedSymbolInfo['symbolInfo'] | undefined): string {
  const { maxPrecision } = resolvePrecisionConfig(symbolInfo)
  return price.toFixed(maxPrecision)
}

export function roundAndFormatPrice(price: number, symbolInfo: MergedSymbolInfo['symbolInfo'] | undefined): string {
  const rounded = roundToDisplayTick(price, symbolInfo)
  return formatPrice(rounded, symbolInfo)
}
